import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

/**
 * useInfiniteScroll
 *
 * What it does (plain English):
 * - You give it a ref on the element that actually scrolls (overflow: auto / scroll).
 * - When the user scrolls near the bottom, it calls your `onLoadMore` once.
 * - It does NOT fetch data itself. You pass `hasMore`, `isLoadingMore`, and `onLoadMore`
 *   from your container / Redux — the hook only decides *when* to call `onLoadMore`.
 *
 * Typical usage:
 *   const scrollRef = useRef<HTMLDivElement>(null);
 *   useInfiniteScroll({
 *     scrollContainerRef: scrollRef,
 *     hasMore: items.length < totalFromApi,
 *     isLoadingMore: requestStatus === Pending,
 *     onLoadMore: () => dispatch(fetchNextPage()),
 *   });
 *   return <div ref={scrollRef} style={{ overflow: 'auto' }}>...</div>;
 *
 * If the scroll box mounts later (modal, dropdown), bump `rebindKey` when it opens
 * so the scroll listener attaches to the real DOM node.
 */

export type UseInfiniteScrollParams = {
  /** Ref attached to the scrollable element (the one with overflow + scrollbars). */
  scrollContainerRef: RefObject<HTMLElement | null>;

  /** You decide: usually `loadedCount < total` or API `hasNextPage`. When false, we never call onLoadMore. */
  hasMore: boolean;

  /** Your function: dispatch next page, append rows, etc. Hook only invokes this. */
  onLoadMore: () => void;

  /** When true, we skip calling onLoadMore (you are already loading the next page). */
  isLoadingMore?: boolean;

  /**
   * How many pixels from the bottom counts as "reached the end".
   * Same idea as email-authentication-table (200px). Increase if you want earlier prefetch.
   */
  thresholdPx?: number;

  /**
   * Master switch. When false, no scroll listener and no onLoadMore (e.g. tab hidden, empty state).
   */
  enabled?: boolean;

  /**
   * Change this value when the scroll container appears later (e.g. `isOpen`, `step`, or `Date.now()`).
   * Otherwise the effect may run once while ref.current is still null and never attach.
   */
  rebindKey?: string | number;
};

/**
 * Subscribes to scroll on `scrollContainerRef` and calls `onLoadMore` when near bottom.
 * Returns nothing — all behavior is side effects; data stays in parent/state/Redux.
 */
export function useInfiniteScroll({
  scrollContainerRef,
  hasMore,
  onLoadMore,
  isLoadingMore = false,
  thresholdPx = 200,
  enabled = true,
  rebindKey,
}: UseInfiniteScrollParams): void {
  // Always point to the latest onLoadMore so the scroll listener does not use a stale closure.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // Latest flags read inside the scroll handler (avoids re-attaching listener every render).
  const stateRef = useRef({
    hasMore,
    isLoadingMore,
    enabled,
    thresholdPx,
  });
  stateRef.current = {
    hasMore,
    isLoadingMore,
    enabled,
    thresholdPx,
  };

  // One stable function the DOM listener always calls; it reads fresh state from refs.
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = el;
    const { hasMore: more, isLoadingMore: loadingMore, enabled: on, thresholdPx: threshold } =
      stateRef.current;

    if (!on || !more || loadingMore) {
      return;
    }

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    // User is "at the bottom" when within thresholdPx of the end (same pattern as email-authentication-table).
    if (distanceFromBottom <= threshold) {
      onLoadMoreRef.current();
    }
  }, [scrollContainerRef]);

  const handleScrollRef = useRef(handleScroll);
  handleScrollRef.current = handleScroll;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !enabled) {
      return undefined;
    }

    const listener = () => {
      handleScrollRef.current();
    };

    el.addEventListener('scroll', listener, { passive: true });
    return () => {
      el.removeEventListener('scroll', listener);
    };
  }, [scrollContainerRef, enabled, rebindKey]);
}
